<template>
  <div>
    <popup-hint hint="assembly">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Assembly">
          <template v-slot:left v-if="incompleteCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{incompleteCount}} incomplete {{incompleteCount>1?'assemblies':'assembly'}}
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.completeStatus='0';filters.itemId=null;filters.from=null;filters.to=null;filters.number=''})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>

          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <production-assembly-preferences/>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Item assemblies are done here.
      </template>
    </popup-hint>

    <purtrak-table name="assembly" objectType="assemblies" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="assemblies2" v-model="selected"
                   fields="id,date,number,itemId,itemName,lot,duration,comments,completed,value,quantity,trackUnits,precision"
                   totalFields="totalValue"
                   :totals.sync="totals"
                   :listFilters="listFilters"
                   @dblclick="assembly => $router.push({name:'ProductionAssemblyEdit',params:{assemblyId:assembly.id}})"
                   :item-class="assembly => (assembly.completed ? '' : 'warning--text')"
                   @itemExpanded="({item, value}) => expandAssembly(item, value)"
                   title="Assembly"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',filters.completeStatus!=null?(filters.completeStatus=='1'?'Completed':'Incomplete'):'',filters.itemId!=null?itemName:'']}"
                   download-endpoint="assembliesExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.completeStatus=null;filters.itemId=null;filters.from=null;filters.to=null;filters.number=''">
          <v-list-item>
            <filter-search label="Number" v-model="filters.number"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item v-if="preferences.assemblyIncomplete">
            <v-autocomplete label="Completed" dense outlined :items="[{value:null,text:'All'},{divider:true},{value:'1',text:'Completed'},{value:'0',text:'Incomplete'}]" v-model="filters.completeStatus"/>
          </v-list-item>
          <v-list-item>
          <v-autocomplete label="Item" dense outlined :items="items" v-model="filters.itemId"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.number!=''" close small class="ml-1" @click:close="filters.number=''">#: {{filters.number}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.completeStatus!=null" close small class="ml-1" @click:close="filters.completeStatus=null">{{filters.completeStatus=='1'?'Completed':'Incomplete'}}</v-chip>
          <v-chip v-show="filters.itemId!=null" close small class="ml-1" @click:close="filters.itemId=null">{{itemName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu v-if="!readOnly">
        <v-list-item :to="{name:'ProductionAssemblyEditNew'}"><v-list-item-title>Create assembly</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="printAssemblyBatch()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.date="{item,printing,value}">
        {{value | formatDateTime}}
        <div v-if="!item.completed && !printing" class="text-caption"><em>INCOMPLETE</em></div>
      </template>
      <template v-slot:item.itemName="{item,printing}">
        {{item.itemName}}
        <div class="text-caption" v-if="!printing">{{item.comments}}</div>
      </template>
      <template v-slot:item.duration="{value}">
        {{value | duration}}
      </template>
      <template v-slot:item.quantity="{item}">
        {{item.quantity | number(item.precision)}} {{item.trackUnits}}
      </template>
      <template v-slot:item.completed="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.value="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.lot="{item,value,printing}">
        <template v-if="printing">{{value}}</template>
        <v-hover v-else-if="item.lot" v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Assembly',to:{name:'ProductionAssembly'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.lot,forward:0}})"
             :class="{'text-decoration-underline':hover}">{{item.lot}}</a>
        </v-hover>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'ProductionAssemblyEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Assembly operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ProductionAssemblyEdit',params:{assemblyId:item.id}}"><v-list-item-title>Details</v-list-item-title></v-list-item>
            <v-list-item @click="printAssembly(item.id,null,true)"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Assembly',to:{name:'ProductionAssembly'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.lot,forward:0}}"><v-list-item-title>Trace lot</v-list-item-title></v-list-item>
            <v-list-item v-if="audit" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item @click="updateComments(item)"><v-list-item-title>Update comments</v-list-item-title></v-list-item>
              <v-list-item @click="assembly=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete assembly</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-16 text-caption" :class="expansionPanelClass" style="width:70%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="40%">BOM</th>
                <th :class="expansionPanelClass" width="10%">Lot</th>
                <th class="text-right" :class="expansionPanelClass" width="30%">{{item.completed?'':'Pending '}}Quantity</th>
                <th class="text-right" :class="expansionPanelClass" width="20%" v-if="values && item.completed">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(assemblyItem,index) of item.items" :key="index">
                <td class="text-caption">{{assemblyItem.name}}</td>
                <td class="text-caption">
                  <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Assembly',to:{name:'ProductionAssembly'}}]},query:{to:'reports/inventory/ReportsLot',id:assemblyItem.id,lot:assemblyItem.lot,forward:0}})">{{assemblyItem.lot}}</a>
                </td>
                <td class="text-caption text-right">{{assemblyItem.quantity | number(assemblyItem.precision)}} {{assemblyItem.units}}</td>
                <td class="text-caption text-right" v-if="values && item.completed">{{assemblyItem.value | currency}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected assemblies':'this assembly'" @delete="performDelete(assembly.id)"/>
    <component :is="dialog" v-bind="dialogProperties" @done="dialog = null"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import productionAssemblyPrintAssembly from '@/views/production/assembly/productionAssemblyPrintAssembly'
import ProductionAssemblyPreferences from '@/views/production/assembly/ProductionAssemblyPreferences'

export default {
  name: 'ProductionAssembly',
  mixins: [productionAssemblyPrintAssembly,performDelete],
  components: {ProductionAssemblyPreferences},
  data() {
    return {
      incompleteCount: 0,
      showStatus: false,
      filters: {
        number: '',
        from: null,
        to: null,
        future: '1',
        completeStatus: null,
        itemId: null
      },
      dateDisplay: '',
      preferences: {},
      assembly: {},
      items: [{text:'All items',value:null},{divider:true}],
      totals: {totalValue: 0},
      showDelete: false,
      deleteMultiple: false,
			dialog: null,
      dialogProperties: {},
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'number', visible:true},
                       { value: 'itemName', visible:true},
                       { value: 'comments', visible:false},
                       { value: 'completed', visible:false},
                       { value: 'duration' ,visible:false},
                       { value: 'lot', visible:true},
                       { value: 'quantity', visible:true},
                       { value: 'value', visible:true}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: '#', value: 'number', width:'10%',sortable:true},
                { text: 'ITEM', value: 'itemName', width:'25%',sortable:true},
                { text: 'COMPLETED', value: 'completed', width:'10%',sortable:true,align:'center'},
                { text: 'COMMENTS', value: 'comments', width:'25%',sortable:false},
                { text: 'LOT', value: 'lot', width:'10%',sortable:true},
                { text: 'DURATION', value: 'duration', width:'15%',sortable:true, omit:() => !this.preferences.assemblyDuration},
                { text: 'QUANTITY', value: 'quantity', width:'15%',sortable:true,align:'right'},
                { text: 'VALUE', value: 'value', width:'10%',sortable:true,align:'right', omit:() => !this.values}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    values() {
      return this.company.permissions.includes('values');
    },
    audit() {
      return this.company.allowAudit && this.company.permissions.includes('audit');
    },
    itemName() {
      return this.items.find(item => item.value === this.filters.itemId)?.text;
    },
    listFilters() {
      const filters = [];
      if (this.filters.number)
        filters.push({field:'number', comparator:'contains', value:this.filters.number});
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.completeStatus == '1' || this.filters.completeStatus == '0')
        filters.push({field:'completed', comparator:'eq', value:this.filters.completeStatus == '1' ? 'true' : 'false'});
      if (this.filters.productId)
        filters.push({field:'itemId', comparator:'eq', value:this.filters.itemId});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.loading = true;
    const items = [];
    await this.$lazyListLoad(items, 'items2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'},{field:'assembled',comparator:'eq',value:'true'}]);
    this.items = this.items.concat(items.map(p => ({text:p.name,value:p.id})));

    const results = await this.$apiCall(`query ProductionAssembly {
                                              assemblingPreferences {
                                                assemblyDuration,assemblyIncomplete
                                              }
                                            }`,
                                        {});
    if (!results.error)
      this.preferences = results.data.assemblingPreferences;
    await this.getStatus();
    this.loading = false;

    if (!['ProductionAssemblyEdit', 'ProductionAssemblyEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.incompleteCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    async getStatus() {
      const results = await this.$apiCall(`query ProductionAssembly {
                                             assembliesIncomplete
                                          }`);
      if (!results.error)
        this.incompleteCount = results.data.assembliesIncomplete;
    },
    async expandAssembly(item, value) {
      if (value && !item.items) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionAssembly($id:ID!) {
                                                  assembly(id:$id) {
                                                    bom{name,id,quantity,units,precision,value,lot}
                                                  }
                                                }`,
                                            {id:item.id});
        const assembly = results.data.assembly;
        this.$set(item, 'items', assembly.bom);
        this.loading = false;
      }
    },
    updateComments(assembly) {
      this.dialogProperties = {item:assembly, operation:'assemblyUpdateComments'};
      this.dialog = () => import(`@/views/production/ProductionUpdateComments`);
    },
    async printAssemblyBatch() {
      const lines = [];
      let n = this.selected.length;
      for (const assembly of this.selected) {
        lines.push(... await this.printAssembly(assembly.id, null, false));
        if (--n)
          lines.push(`<div class="page-break"/>`);
      }
      this.printHtml('Assembly',lines.join(''));
    }
  }
}
</script>